/**
 * Input component — Brand Book §2.3
 * bg-muted background, 36px height, 0px radius
 * Orange dot (•) for required, orange focus border + 2px ring
 * Error = red border + red 2px ring + red text (no red bg)
 * Disabled = 40% opacity
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, rightIcon, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-body-sm font-semibold text-txt-secondary mb-1"
          >
            {label}
            {props.required && (
              <span className="text-orange ml-1">•</span>
            )}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-txt-tertiary">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx(
              'w-full h-9 px-3 border bg-surface-muted text-txt-primary',
              'placeholder:text-txt-tertiary',
              'font-sans text-body font-normal',
              'transition-[border-color,box-shadow] duration-200 ease',
              'hover:border-border-strong',
              'focus:outline-none focus:border-orange focus:shadow-[0_0_0_2px_var(--orange-light)]',
              'disabled:opacity-40 disabled:cursor-not-allowed',
              error
                ? 'border-error shadow-[0_0_0_2px_var(--error-light)]'
                : 'border-border',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-txt-tertiary">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-caption text-error">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1 text-caption text-txt-tertiary">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';